import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { Layers, TrendingUp, BarChart3, ArrowRight, RefreshCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const ForecastComparison = () => {
  const [stores, setStores] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparisonType, setComparisonType] = useState('stores');
  const [selectedItems, setSelectedItems] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [activeView, setActiveView] = useState('bar');

  useEffect(() => {
    fetchDynamicData();
  }, []);

  const fetchDynamicData = async () => {
    try {
      const token = localStorage.getItem('token');
      const h = { 'Authorization': `Bearer ${token}` };
      const [storeRes, catRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/forecasting/dynamic/stores`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/forecasting/dynamic/categories`, { headers: h })
      ]);
      setStores(storeRes.data?.stores || []);
      setCategories(catRes.data?.categories || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev.includes(item);
      if (exists) return prev.filter(i => i !== item);
      if (prev.length >= 6) return prev;
      return [...prev, item];
    });
  };

  const handleCompare = async () => {
    if (selectedItems.length < 2) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const h = { 'Authorization': `Bearer ${token}` };
      const results = [];

      if (comparisonType === 'stores') {
        const promises = selectedItems.map(storeId =>
          axios.get(`${BACKEND_URL}/api/forecasting/professional/store-operations?store_id=${storeId}`, { headers: h })
        );
        const responses = await Promise.all(promises);
        responses.forEach((res, idx) => {
          const sf = res.data?.store_forecasts?.[0];
          if (sf) {
            results.push({
              id: selectedItems[idx],
              name: sf.store_name || `Store ${selectedItems[idx]}`,
              revenue_7d: sf.forecast_7d?.revenue || 0,
              revenue_30d: sf.forecast_30d?.revenue || 0,
              revenue_90d: sf.forecast_90d?.revenue || 0,
              units_30d: sf.forecast_30d?.units || 0,
              footfall_30d: sf.forecast_30d?.footfall || 0,
              conversion: sf.forecast_30d?.conversion || 0,
              risk: sf.risk_score || 'low',
              trend: sf.trend || 'stable'
            });
          }
        });
      } else if (comparisonType === 'horizons') {
        const res = await axios.get(`${BACKEND_URL}/api/forecasting/professional/standard?metric=sales`, { headers: h });
        const forecasts = res.data?.forecasts || {};
        Object.entries(forecasts).forEach(([key, f]) => {
          results.push({
            id: key,
            name: `${f.horizon_days} Days`,
            days: f.horizon_days,
            revenue: f.forecasted_revenue || 0,
            units: f.forecasted_units || 0,
            margin: f.forecasted_margin,
            margin_status: f.margin_status || (f.forecasted_margin != null ? 'OK' : 'INSUFFICIENT_COST_DATA'),
            confidence: f.confidence_level || 'medium',
            stockout_risk: f.stockout_risk || 'low',
            daily_revenue: (f.forecasted_revenue || 0) / f.horizon_days
          });
        });
      } else if (comparisonType === 'categories') {
        const promises = selectedItems.map(cat =>
          axios.get(`${BACKEND_URL}/api/forecasting/professional/category-planner?category=${encodeURIComponent(cat)}`, { headers: h })
        );
        const responses = await Promise.all(promises);
        responses.forEach((res, idx) => {
          const data = res.data;
          const hs = data?.historical_summary || {};
          results.push({
            id: selectedItems[idx],
            name: selectedItems[idx],
            daily_units: hs.avg_daily_sales_units || 0,
            daily_value: hs.avg_daily_sales_value || 0,
            margin_pct: hs.current_margin_pct,
            margin_status: hs.margin_status || (hs.current_margin_pct != null ? 'OK' : 'INSUFFICIENT_COST_DATA'),
            inventory: hs.current_inventory_units || 0,
            sell_through: hs.sell_through_rate,
            sell_through_status: hs.sell_through_status || (hs.sell_through_rate != null ? 'OK' : 'INSUFFICIENT_INVENTORY_DATA'),
            categories_count: data?.summary?.total_categories || 0
          });
        });
      }

      setComparisonData(results);
    } catch (err) {
      console.error('Comparison error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStoreComparison = () => {
    if (!comparisonData || comparisonData.length === 0) return null;

    const revenueData = [
      { name: '7-Day', ...Object.fromEntries(comparisonData.map(s => [s.name, s.revenue_7d])) },
      { name: '30-Day', ...Object.fromEntries(comparisonData.map(s => [s.name, s.revenue_30d])) },
      { name: '90-Day', ...Object.fromEntries(comparisonData.map(s => [s.name, s.revenue_90d])) },
    ];

    const radarData = comparisonData.map(s => ({
      name: s.name,
      Revenue: Math.min(100, (s.revenue_30d / Math.max(...comparisonData.map(d => d.revenue_30d))) * 100),
      Units: Math.min(100, (s.units_30d / Math.max(...comparisonData.map(d => d.units_30d || 1))) * 100),
      Footfall: Math.min(100, (s.footfall_30d / Math.max(...comparisonData.map(d => d.footfall_30d || 1))) * 100),
      Conversion: s.conversion * 10
    }));

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {comparisonData.map((s, idx) => (
            <Card key={s.id} className="border-l-4" style={{ borderLeftColor: COLORS[idx] }}>
              <CardContent className="pt-4 pb-3 px-3">
                <p className="text-xs text-slate-500 truncate">{s.name}</p>
                <p className="text-lg font-bold mt-1">₹{(s.revenue_30d / 100000).toFixed(1)}L</p>
                <div className="flex items-center gap-1 mt-1">
                  <Badge className={s.risk === 'high' ? 'bg-red-100 text-red-800 text-xs' : s.risk === 'medium' ? 'bg-yellow-100 text-yellow-800 text-xs' : 'bg-green-100 text-green-800 text-xs'}>
                    {s.risk}
                  </Badge>
                  <span className="text-xs text-slate-500">{s.trend}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList>
            <TabsTrigger value="bar">Bar Chart</TabsTrigger>
            <TabsTrigger value="radar">Radar</TabsTrigger>
            <TabsTrigger value="table">Table</TabsTrigger>
          </TabsList>

          <TabsContent value="bar">
            <Card>
              <CardHeader>
                <CardTitle>Revenue Forecast Comparison by Horizon</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                      <Tooltip formatter={(value) => `₹${Number(value).toLocaleString()}`} />
                      <Legend />
                      {comparisonData.map((s, idx) => (
                        <Bar key={s.id} dataKey={s.name} fill={COLORS[idx]} radius={[4, 4, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="radar">
            <Card>
              <CardHeader>
                <CardTitle>Multi-Metric Store Comparison</CardTitle>
                <CardDescription>Normalized scores (0-100) across revenue, units, footfall, and conversion</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={[
                      { metric: 'Revenue', ...Object.fromEntries(radarData.map(r => [r.name, r.Revenue])) },
                      { metric: 'Units', ...Object.fromEntries(radarData.map(r => [r.name, r.Units])) },
                      { metric: 'Footfall', ...Object.fromEntries(radarData.map(r => [r.name, r.Footfall])) },
                      { metric: 'Conversion', ...Object.fromEntries(radarData.map(r => [r.name, r.Conversion])) },
                    ]}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="metric" />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      {comparisonData.map((s, idx) => (
                        <Radar key={s.id} name={s.name} dataKey={s.name} stroke={COLORS[idx]} fill={COLORS[idx]} fillOpacity={0.15} strokeWidth={2} />
                      ))}
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="table">
            <Card>
              <CardContent className="pt-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="comparison-table">
                    <thead>
                      <tr className="border-b bg-slate-50 dark:bg-slate-800">
                        <th className="px-4 py-3 text-left">Store</th>
                        <th className="px-4 py-3 text-right">7d Revenue</th>
                        <th className="px-4 py-3 text-right">30d Revenue</th>
                        <th className="px-4 py-3 text-right">90d Revenue</th>
                        <th className="px-4 py-3 text-right">30d Units</th>
                        <th className="px-4 py-3 text-right">Footfall</th>
                        <th className="px-4 py-3 text-center">Risk</th>
                        <th className="px-4 py-3 text-center">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((s, idx) => (
                        <tr key={s.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="px-4 py-3 font-medium">
                            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[idx] }}></span>
                            {s.name}
                          </td>
                          <td className="px-4 py-3 text-right">₹{s.revenue_7d.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-right font-semibold text-green-600">₹{s.revenue_30d.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-right">₹{s.revenue_90d.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-4 py-3 text-right">{s.units_30d.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">{s.footfall_30d.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={s.risk === 'high' ? 'bg-red-100 text-red-800' : s.risk === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                              {s.risk}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center capitalize">{s.trend}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  const renderHorizonComparison = () => {
    if (!comparisonData || comparisonData.length === 0) return null;

    return (
      <div className="space-y-6">
        {/* Horizon Overlay Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Multi-Horizon Revenue Overlay</CardTitle>
            <CardDescription>Revenue forecast across all horizons with daily breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={comparisonData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorMargin" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`} />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => v.toLocaleString()} />
                  <Tooltip formatter={(value, name) => name.includes('Revenue') || name.includes('Margin') ? `₹${Number(value).toLocaleString()}` : value.toLocaleString()} />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#3b82f6" fill="url(#colorRevenue)" strokeWidth={2} />
                  <Area yAxisId="left" type="monotone" dataKey="margin" name="Margin" stroke="#10b981" fill="url(#colorMargin)" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="units" name="Units" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Daily Revenue Efficiency */}
        <Card>
          <CardHeader>
            <CardTitle>Daily Revenue Efficiency by Horizon</CardTitle>
            <CardDescription>Revenue per day across different forecast horizons</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString()}`} />
                  <Bar dataKey="daily_revenue" name="Daily Revenue" radius={[6, 6, 0, 0]}>
                    {comparisonData.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Confidence & Risk Table */}
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 dark:bg-slate-800">
                    <th className="px-4 py-3 text-left">Horizon</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Units</th>
                    <th className="px-4 py-3 text-right">Margin</th>
                    <th className="px-4 py-3 text-right">Daily Revenue</th>
                    <th className="px-4 py-3 text-center">Confidence</th>
                    <th className="px-4 py-3 text-center">Stockout Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((d, idx) => (
                    <tr key={d.id} className="border-b">
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[idx] }}></span>
                        {d.name}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">₹{(d.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="px-4 py-3 text-right">{(d.units || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">{d.margin != null ? `₹${d.margin.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Cost Data Missing</span>}</td>
                      <td className="px-4 py-3 text-right">₹{d.daily_revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="px-4 py-3 text-center"><Badge variant="outline">{d.confidence}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={d.stockout_risk === 'high' ? 'bg-red-100 text-red-800' : d.stockout_risk === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                          {d.stockout_risk}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="forecast-comparison-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Layers className="h-6 w-6 text-blue-600" />
          Forecast Comparison
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Compare forecasts across stores, categories, or horizons side-by-side
        </p>
      </div>

      {/* Configuration Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Comparison Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end flex-wrap">
            <div className="space-y-2 min-w-[180px]">
              <Label>Compare By</Label>
              <Select value={comparisonType} onValueChange={(v) => { setComparisonType(v); setSelectedItems([]); setComparisonData(null); }}>
                <SelectTrigger data-testid="comparison-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stores">Stores</SelectItem>
                  <SelectItem value="horizons">Forecast Horizons</SelectItem>
                  <SelectItem value="categories">Categories</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {comparisonType !== 'horizons' && (
              <div className="flex-1">
                <Label>Select Items (2-6)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {comparisonType === 'stores' && stores.map(s => (
                    <Badge
                      key={s.store_id}
                      variant={selectedItems.includes(s.store_id) ? 'default' : 'outline'}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleItem(s.store_id)}
                      data-testid={`store-chip-${s.store_id}`}
                    >
                      {s.store_id} - {s.store_name?.substring(0, 15)}
                    </Badge>
                  ))}
                  {comparisonType === 'categories' && categories.map(cat => (
                    <Badge
                      key={cat}
                      variant={selectedItems.includes(cat) ? 'default' : 'outline'}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => toggleItem(cat)}
                    >
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={handleCompare}
              disabled={loading || (comparisonType !== 'horizons' && selectedItems.length < 2)}
              data-testid="compare-btn"
            >
              {loading ? (
                <RefreshCcw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Comparing...' : 'Compare'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {comparisonType === 'stores' && renderStoreComparison()}
      {comparisonType === 'horizons' && renderHorizonComparison()}
      {comparisonType === 'categories' && comparisonData && comparisonData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Category Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="daily_value" name="Daily Sales Value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 dark:bg-slate-800">
                    <th className="px-4 py-3 text-left">Category</th>
                    <th className="px-4 py-3 text-right">Daily Units</th>
                    <th className="px-4 py-3 text-right">Daily Value</th>
                    <th className="px-4 py-3 text-right">Margin %</th>
                    <th className="px-4 py-3 text-right">Inventory</th>
                    <th className="px-4 py-3 text-right">Sell-Through</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((d, idx) => (
                    <tr key={d.id} className="border-b">
                      <td className="px-4 py-3 font-medium">
                        <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[idx] }}></span>
                        {d.name}
                      </td>
                      <td className="px-4 py-3 text-right">{(d.daily_units || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">₹{(d.daily_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                      <td className="px-4 py-3 text-right">{d.margin_pct != null ? `${d.margin_pct}%` : <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Cost Data Missing</span>}</td>
                      <td className="px-4 py-3 text-right">{Number(d.inventory || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">{d.sell_through != null ? `${d.sell_through}%` : <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">N/A</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!comparisonData && !loading && (
        <Card>
          <CardContent className="py-16 text-center">
            <Layers className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">Select items and click Compare</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Choose a comparison type, select 2-6 items, and generate a side-by-side analysis with interactive charts, radar visualization, and detailed tables.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ForecastComparison;
